import _ from 'lodash';
import commaNumber from 'comma-number';

import { getPrettyDurationPrecise, getPrettyElapsedShort, getPrettyTimeLeftShort } from './format';

/*
  Derives everything the transcript renders for a tool_call row's mid-tool
  progress timeline (`progressUpdates` — node-stamped { at, seq, … } entries
  appended by toolcall.progress_update events; the moments webhook does not
  relay these yet, so this path is dormant here). Pure: the same row + the
  same `now` always produce the same output, so the card can re-derive on a
  1s heartbeat while a tool runs and the ETA ages between frames.
*/

const STALL_AFTER_MS     = 45000; // no tick for this long while unresolved → "Still working…"
const AGENT_ETA_FRESH_MS = 20000; // an agent-supplied etaSeconds beats extrapolation while this fresh
const ETA_SLOPE_WINDOW   = 5;     // pct-bearing points the rate is fit over
const ETA_MIN_SPAN_MS    = 3000;  // refuse a rate from a near-instant window

// The card's one input → one output. `results` are the row's tool_result children
// (completion always wins); `thread` supplies cancelRequestedAt for the Stopped state.
export const deriveToolCallProgress = (call, { results = [], thread = null, now = Date.now() } = {}) => {
  const updates = sortUpdates(call && call.progressUpdates);
  const hasTimeline = updates.length > 0;
  const isDone = Array.isArray(results) && results.length > 0;
  const latest = hasTimeline ? updates[updates.length - 1] : null;

  const pct = displayPct(updates);
  const status = deriveStatus({ call, isDone, hasTimeline, latest, thread, now });

  return {
    hasTimeline,
    status,
    pct,
    isIndeterminate: hasTimeline && pct === null,
    label: latestLabel(updates),
    detail: (latest && latest.detail) || null,
    counts: (latest && latest.counts) || null,
    countsText: formatProgressCounts(latest && latest.counts),
    etaText: status === 'running' ? deriveEtaText(updates, pct, now) : null,
    elapsedText: deriveElapsedText({ updates, results, isDone }),
    updates
  };
};

// Per-entry percentage: an explicit progressPct wins; counts supply the fraction
// when the agent couldn't quantify. Null when the entry carries neither.
export const getProgressEntryPct = entry => {
  if (!entry) return null;
  if (typeof entry.progressPct === 'number' && Number.isFinite(entry.progressPct)) {
    return _.clamp(entry.progressPct, 0, 100);
  }
  const counts = entry.counts;
  if (counts && typeof counts.done === 'number' && typeof counts.total === 'number' && counts.total > 0) {
    return _.clamp((counts.done / counts.total) * 100, 0, 100);
  }
  return null;
};

// "412 of 1,240 creators" — null when the entry has no usable counts.
export const formatProgressCounts = counts => {
  if (!counts || typeof counts.done !== 'number' || typeof counts.total !== 'number' || counts.total <= 0) return null;
  const base = `${commaNumber(counts.done)} of ${commaNumber(counts.total)}`;
  return counts.unit ? `${base} ${counts.unit}` : base;
};

const sortUpdates = updates => {
  if (!Array.isArray(updates)) return [];
  return _.sortBy(updates.filter(u => u && typeof u.seq === 'number'), 'seq');
};

const entryTime = entry => new Date(entry && entry.at).getTime();

// Display percentage is monotonic — the max ever reported — so an honest
// stage restart in the data can never visibly walk the bar backwards.
const displayPct = updates => {
  const pcts = updates.map(getProgressEntryPct).filter(p => p !== null);
  return pcts.length ? Math.max(...pcts) : null;
};

const latestLabel = updates => {
  const withLabel = [...updates].reverse().find(u => u.progressLabel);
  return (withLabel && withLabel.progressLabel) || null;
};

// The cancelled / stalled treatments only apply to cards with a timeline — a
// plain breadcrumb without progress keeps its existing running-until-result look.
const deriveStatus = ({ call, isDone, hasTimeline, latest, thread, now }) => {
  if (isDone) return 'done';
  const base = String(_.get(call, 'metadata.status') || 'running').toLowerCase();
  if (base !== 'running') return base;
  if (hasTimeline && thread && thread.cancelRequestedAt) return 'cancelled';
  if (hasTimeline && now - entryTime(latest) > STALL_AFTER_MS) return 'stalled';
  return 'running';
};

// Buckets are deliberately coarse (getPrettyTimeLeftShort) — a glance, not a
// countdown — so the line reads calm while the bar carries the precision.
const deriveEtaText = (updates, pct, now) => {
  if (pct === null || pct >= 100) return null;
  const remainingMs = deriveRemainingMs(updates, pct, now);
  if (remainingMs === null) return null;
  const magnitude = getPrettyTimeLeftShort(remainingMs);
  if (!magnitude) return null;
  return magnitude === 'under a minute' ? 'under a minute left' : `about ${magnitude} left`;
};

const deriveRemainingMs = (updates, pct, now) => {
  // A fresh agent-supplied estimate beats extrapolation — the tool knows its own plan.
  const withEta = [...updates].reverse().find(u => typeof u.etaSeconds === 'number');
  if (withEta && now - entryTime(withEta) < AGENT_ETA_FRESH_MS) {
    return Math.max(0, withEta.etaSeconds * 1000 - (now - entryTime(withEta)));
  }

  const points = updates
    .map(u => ({ t: entryTime(u), pct: getProgressEntryPct(u) }))
    .filter(p => p.pct !== null && Number.isFinite(p.t));
  const window = points.slice(-ETA_SLOPE_WINDOW);
  if (window.length < 2) return null;

  const first = window[0];
  const last = window[window.length - 1];
  const spanMs = last.t - first.t;
  const climbed = last.pct - first.pct;
  // No number beats a made-up number: refuse a rate from a flat or near-instant window.
  if (spanMs < ETA_MIN_SPAN_MS || climbed <= 0) return null;

  const msPerPct = spanMs / climbed;
  const remainingAtLast = (100 - pct) * msPerPct;
  // Anchor at the last point and age as wall-clock advances, so the bucket
  // steps down between frames.
  return Math.max(0, remainingAtLast - (now - last.t));
};

// The result's own durationMs is authoritative; the timeline span is the fallback.
// Every completed call shows its duration — per-call timing is the dock's debugging
// surface for slow tools, so even instant calls earn a number.
const deriveElapsedText = ({ updates, results, isDone }) => {
  if (!isDone) return null;
  const durationMs = _.get(results, '0.metadata.durationMs');
  if (Number.isFinite(durationMs)) return getPrettyDurationPrecise(durationMs);
  if (!updates.length) return null;
  return getPrettyElapsedShort(entryTime(updates[updates.length - 1]) - entryTime(updates[0]));
};
